/*
  Loaded once from layout/theme.liquid:
    <script src="{{ 't10-chat-cta.js' | asset_url }}" defer></script>

  Opens the Talk to Bear panel (snippets/t10-bear-panel.liquid) from any
  "Talk to Hi-Fi Bear" button:
    - elements with data-open-chat (the t10-talk-to-bear-button snippet), and
    - any link whose URL contains #talk-to-bear (theme-editor and Replo buttons
      set to /pages/book-a-meeting#talk-to-bear).
  The panel offers Message Bear (opens Shopify Inbox, below), Call Bear (hidden
  until CALL_NUMBER is set) and Email Bear. Without the panel markup, the button
  opens Inbox directly.

  Every click pushes {event: 'chat_cta_click', cta_location: '...'} to dataLayer.
  cta_location comes from data-cta-location, else a ?cta= parameter in the link
  (theme-editor buttons, e.g. /pages/book-a-meeting?cta=home_commission#talk-to-bear),
  else the section key.
  The panel then pushes one of:
    {event: 'bear_contact_choice', contact_method: 'message'|'call'|'email', cta_location}
    {event: 'bear_panel_dismiss', cta_location}  (closed by ×, backdrop or Escape)
  and tags Microsoft Clarity: events bear_panel_open, bear_contact_<method>,
  bear_panel_dismiss, and the session tag bear_contact=<method>.

  Shopify Inbox's own floating Chat bubble also opens the panel, tracked as
  cta_location 'chat_bubble'. The bubble opens Inbox directly when Inbox is
  already open, after a Message choice this browser session (sessionStorage),
  and when the panel markup is missing. Only trusted (visitor) clicks are
  intercepted, so this script's own launcher click below goes through.

  A visit with #talk-to-bear in the URL (the fallback below) opens Inbox directly.

  UNOFFICIAL: Shopify does not document a way to open Inbox from page code.
  This calls show() on the <shopify-chat> element, then falls back to clicking
  the launcher inside its shadow root.
  Off the booking page: if chat has not opened within WAIT_MS, the visitor follows
  the link to /pages/book-a-meeting, where this script tries again on load.
  On the booking page: it never navigates; it keeps waiting, then shows an
  inline email notice next to the clicked button.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-open-chat], a[href*="#talk-to-bear"]';
  var HASH = '#talk-to-bear';
  var FALLBACK = '/pages/book-a-meeting';
  var WAIT_MS = 5000;
  var BOOKING_WAIT_MS = 12000;
  // Published in the site footer and contact page. Two different phone numbers
  // are published (press vs booking page), so the notice offers email only.
  var EMAIL = 'concierge@earmicro.com';
  // Call Bear button number. A CallRail tracking number goes here, e.g. '+1 573 555 0100'.
  // While this is empty, the Call Bear button stays hidden.
  var CALL_NUMBER = '+1 816 451 0040';
  // sessionStorage key set by a Message choice.
  var MESSAGE_KEY = 't10_bear_message';

  function onBookingPage() {
    return window.location.pathname.replace(/\/$/, '') === FALLBACK;
  }

  function ctaLocation(el) {
    if (el.getAttribute('data-cta-location')) return el.getAttribute('data-cta-location');
    var href = el.getAttribute('href') || '';
    var m = href.match(/[?&]cta=([A-Za-z0-9_-]+)/);
    if (m) return m[1];
    // Fall back to the section key, e.g.
    // shopify-section-template--20475237433596__4e18cbd6-ffa5-... -> 4e18cbd6
    var section = el.closest('.shopify-section');
    if (!section || !section.id) return 'unknown';
    return section.id.split('__').pop().split('-')[0];
  }

  function push(data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  function track(location) {
    push({ event: 'chat_cta_click', cta_location: location });
  }

  function clarity() {
    if (typeof window.clarity !== 'function') return;
    try { window.clarity.apply(window, arguments); } catch (e) { /* tagging is best effort */ }
  }

  function isOpen(chat) {
    return chat.open === true || chat.hasAttribute('open');
  }

  // Inbox's launcher button. It sits in <shopify-agent-runtime>'s shadow root,
  // inside <shopify-chat>'s; until the runtime loads, a placeholder sits in the
  // outer root.
  function findLauncher(chat) {
    var root = chat && chat.shadowRoot;
    if (!root) return null;
    var sel = 'button.activator, [part="activator"]';
    var runtime = root.querySelector('shopify-agent-runtime');
    // Prefer the real button; the placeholder can linger after the runtime loads.
    return (runtime && runtime.shadowRoot && runtime.shadowRoot.querySelector(sel)) ||
      root.querySelector(sel) || null;
  }

  // Returns true if the chat panel is open after the attempt. state.clicked
  // records a launcher click: the launcher toggles Inbox, so a second click on a
  // later retry could close an open that is still starting. Retries after a
  // click only call show() and poll.
  function tryOpen(state) {
    var chat = document.querySelector('shopify-chat');
    if (!chat) return false;
    var showFailed = typeof chat.show !== 'function';
    try {
      if (!showFailed) chat.show();
    } catch (e) { showFailed = true; }
    if (isOpen(chat)) return true;
    if (showFailed && !state.clicked) {
      var launcher = findLauncher(chat);
      if (launcher) {
        state.clicked = true;
        launcher.click();
      }
    }
    return isOpen(chat);
  }

  // Retries until chat opens or the deadline passes. Resolves true/false.
  // The first attempt waits for the current click to finish: Inbox closes on any
  // click outside it, so a launcher click made during the visitor's click would
  // open Inbox and that same click would close it again.
  function openWhenReady(timeoutMs) {
    return new Promise(function (resolve) {
      var deadline = Date.now() + timeoutMs;
      var state = { clicked: false };
      function attempt() {
        if (tryOpen(state)) return resolve(true);
        if (Date.now() >= deadline) return resolve(false);
        setTimeout(attempt, 250);
      }
      setTimeout(attempt, 0);
    });
  }

  function showNotice(el) {
    if (!el || !el.parentNode) return;
    var next = el.nextElementSibling;
    if (next && next.classList.contains('t10-chat-notice')) return;
    var note = document.createElement('p');
    note.className = 't10-chat-notice';
    note.setAttribute('role', 'status');
    note.style.cssText = 'margin:.75rem 0 0;font-size:.875rem;line-height:1.4;';
    note.appendChild(document.createTextNode("Chat isn't loading. Email T10 Bespoke at "));
    var a = document.createElement('a');
    a.href = 'mailto:' + EMAIL;
    a.textContent = EMAIL;
    a.style.textDecoration = 'underline';
    note.appendChild(a);
    note.appendChild(document.createTextNode('.'));
    el.parentNode.insertBefore(note, el.nextSibling);
  }

  function fallbackUrl(el) {
    var href = el.getAttribute('href');
    return href && href.charAt(0) !== '#' ? href : FALLBACK + HASH;
  }

  // Opens Inbox for the clicked button, with the fallbacks described above.
  // From the bubble, el is in Inbox's shadow DOM, so the notice goes next to the
  // page's first CTA and the fallback is the plain booking link.
  function openChat(el) {
    var light = el && el.getRootNode() === document;
    if (onBookingPage()) {
      openWhenReady(BOOKING_WAIT_MS).then(function (opened) {
        if (!opened) showNotice(light ? el : firstButton());
      });
      return;
    }
    openWhenReady(WAIT_MS).then(function (opened) {
      if (!opened) window.location.href = light ? fallbackUrl(el) : FALLBACK + HASH;
    });
  }

  // Talk to Bear panel. trigger is the button that opened it; focus returns there on close.
  var panel = null;
  var trigger = null;
  var triggerLocation = '';
  var savedOverflow = null;

  function focusables() {
    return Array.prototype.filter.call(
      panel.querySelectorAll('button, a[href]'),
      function (node) { return !node.hidden && node.offsetParent !== null; }
    );
  }

  function onPanelKey(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel(null);
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var inside = panel.contains(document.activeElement);
    if (event.shiftKey && (!inside || document.activeElement === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  function onPanelClick(event) {
    if (event.target.closest('[data-bear-close]')) {
      closePanel(null);
      return;
    }
    var choice = event.target.closest('[data-bear-choice]');
    if (!choice) return;
    var method = choice.getAttribute('data-bear-choice');
    var el = trigger;
    push({ event: 'bear_contact_choice', contact_method: method, cta_location: triggerLocation });
    clarity('set', 'bear_contact', method);
    clarity('event', 'bear_contact_' + method);
    closePanel(method);
    // Call and Email are tel:/mailto: links and follow normally.
    if (method === 'message') {
      rememberMessage();
      openChat(el);
    }
  }

  function setupPanel() {
    var call = panel.querySelector('[data-bear-choice="call"]');
    if (call && CALL_NUMBER) {
      call.href = 'tel:' + CALL_NUMBER.replace(/[^\d+]/g, '');
      call.hidden = false;
    }
    panel.addEventListener('click', onPanelClick);
  }

  // Returns false when the panel markup is missing, so the caller opens Inbox instead.
  function openPanel(el, location) {
    if (!panel) {
      panel = document.getElementById('t10-bear-panel');
      if (!panel) return false;
      setupPanel();
    }
    if (!panel.hidden) return true;
    trigger = el;
    triggerLocation = location;
    var root = document.documentElement;
    savedOverflow = [root.style.overflow, document.body.style.overflow, document.body.style.paddingRight];
    var scrollbar = window.innerWidth - root.clientWidth;
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
    panel.hidden = false;
    // Hides Shopify Inbox's floating launcher (CSS in the panel snippet).
    root.classList.add('t10bp-open');
    document.addEventListener('keydown', onPanelKey, true);
    var first = focusables()[0];
    (first || panel.querySelector('[role="dialog"]')).focus({ preventScroll: true });
    clarity('event', 'bear_panel_open');
    return true;
  }

  // method is the visitor's choice, or null when they closed the panel without one.
  function closePanel(method) {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    // Restore the launcher first: a Message choice opens Inbox right after this.
    document.documentElement.classList.remove('t10bp-open');
    document.removeEventListener('keydown', onPanelKey, true);
    document.documentElement.style.overflow = savedOverflow[0];
    document.body.style.overflow = savedOverflow[1];
    document.body.style.paddingRight = savedOverflow[2];
    if (!method) {
      push({ event: 'bear_panel_dismiss', cta_location: triggerLocation });
      clarity('event', 'bear_panel_dismiss');
    }
    var el = returnTarget(trigger);
    trigger = null;
    if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
  }

  // Replo re-renders after load and can replace the CTA while the panel is open.
  // A detached trigger can't take focus, so use the current bubble or the
  // connected CTA with the same cta_location, else the page's first CTA.
  function returnTarget(el) {
    if (el && el.isConnected) return el;
    if (triggerLocation === 'chat_bubble') {
      var launcher = findLauncher(document.querySelector('shopify-chat'));
      if (launcher) return launcher;
    }
    var buttons = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      if (triggerLocation !== 'unknown' && ctaLocation(buttons[i]) === triggerLocation) return buttons[i];
    }
    return firstButton();
  }

  document.addEventListener('click', function (event) {
    var el = event.target.closest && event.target.closest(SELECTOR);
    if (!el) return;
    var location = ctaLocation(el);
    track(location);
    // Let new-tab clicks behave like normal links.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    // preventDefault also stops the theme's page-fade handler (it checks defaultPrevented).
    event.preventDefault();
    if (!openPanel(el, location)) openChat(el);
  }, true);

  function firstButton() {
    return document.querySelector(SELECTOR);
  }

  // After a Message choice the bubble opens Inbox directly for the rest of the
  // session. Storage can throw (privacy modes); then the panel keeps showing.
  function rememberMessage() {
    try { window.sessionStorage.setItem(MESSAGE_KEY, '1'); } catch (e) { /* best effort */ }
  }

  function choseMessage() {
    try { return window.sessionStorage.getItem(MESSAGE_KEY) === '1'; } catch (e) { return false; }
  }

  // Inbox's bubble is <button part="activator"> two shadow roots inside
  // <shopify-chat>. Clicks are composed, so this capture listener on document
  // runs before Inbox's own handler and can stop it. Enter and Space on the
  // button fire a click too. Only the activator is matched, so clicks inside
  // the open Inbox dialog pass through.
  function bubbleIn(event) {
    var path = event.composedPath ? event.composedPath() : [];
    var activator = null;
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node.getAttribute) continue;
      if (!activator && (' ' + (node.getAttribute('part') || '') + ' ').indexOf(' activator ') !== -1) activator = node;
      if (activator && node.tagName === 'SHOPIFY-CHAT') return { activator: activator, chat: node };
    }
    return null;
  }

  document.addEventListener('click', function (event) {
    // tryOpen's launcher.click() is untrusted and must reach Inbox.
    if (!event.isTrusted) return;
    var hit = bubbleIn(event);
    if (!hit || isOpen(hit.chat) || choseMessage()) return;
    if (!openPanel(hit.activator, 'chat_bubble')) return;
    event.preventDefault();
    event.stopPropagation();
    track('chat_bubble');
  }, true);

  // Arrived via the fallback link, or the hash changed in place: try again.
  function openFromHash() {
    if (window.location.hash !== HASH) return;
    openWhenReady(8000).then(function (opened) {
      if (!opened && onBookingPage()) showNotice(firstButton());
    });
  }

  window.addEventListener('hashchange', openFromHash);
  openFromHash();
})();
